import CustomPreloader from '@components/CustomPreloader'
import LanguageMenu from '@components/LanguageMenu'
import SelectMethod from '@components/SelectMethod'
import { BackIcon } from '@icons'
import {
  CheckoutFlowStep,
  FakeJetonbankBanks,
  isJetonWallet,
  useBanksRequest,
  useCancelPaymentRequest,
  useCheckoutPayment,
  useEventTracker,
  useLanguageParam,
  useLanguages,
  usePaymentOptions,
  useSessionId
} from 'mmfintech-checkout-commons'
import type { CheckoutMethodField, CountryChoice, SupportedBank } from 'mmfintech-checkout-commons/types'
import {
  GlobalContext,
  isSingleItemArray,
  isValidArray,
  isValidFunction,
  isValidString,
  parseJson,
  tr
} from 'mmfintech-commons'
import { Button, ErrorDisplay, Input } from 'mmfintech-portal-commons'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import settings from '../../settings'
import { DepositResult } from '../DepositResult'
import { WarningMessageWrapper } from '../DepositResult/PreviewBankwire/PreviewBankwire.styled'
import { AdditionalFieldsStep } from './AdditionalFieldsStep'
import { ButtonsFooter } from './ButtonsFooter'
import styles from './Checkout.module.scss'
import { CheckoutBody, CheckoutHeader, CheckoutHeaderBack, CheckoutInputWrapper } from './Checkout.styled'
import { CustomerConsentStep } from './CustomerConsentStep'
import { InternalExecutionStep } from './InternalExecutionStep'
import { SelectBankStep } from './SelectBankStep'
import { useFakeBankLogin } from './useFakeBankLogin'

const FAKE_BANK_LOGIN_STEP = 71

export const Checkout = () => {
  const { modalShow } = useContext(GlobalContext)

  useLanguageParam(settings.languages)
  const sessionId = useSessionId()

  const triggeredRef = useRef<boolean>(false)

  const {
    checkoutCountries,
    checkoutPay,
    checkoutPayError,
    checkoutPayFetching,
    session,
    sessionError,
    sessionFetching
  } = useSelector(
    ({
      checkout: {
        checkoutCountries,
        checkoutPay,
        checkoutPayError,
        checkoutPayFetching,
        session,
        sessionError,
        sessionFetching
      }
    }) => ({
      checkoutCountries,
      checkoutPay,
      checkoutPayError,
      checkoutPayFetching,
      session,
      sessionError,
      sessionFetching
    }),
    shallowEqual
  )

  const history = useHistory()

  const { selectedLanguage } = useLanguages()
  const { cancelPayment } = useCancelPaymentRequest()

  const {
    step,
    setStep,
    fieldValues,
    countryCode,
    setCountryCode,
    hasOnlyOneCountry,
    paymentMethod,
    setPaymentMethod,
    selectedPaymentOption,
    handlePay,
    initialized,
    showFieldsAfterBanks,
    jetonBankSpecialCase
  } = useCheckoutPayment({
    initialStep: CheckoutFlowStep.SELECT_METHOD,
    preselectSingleBank: true,
    preselectSingleMethod: true,
    enableSpecialJetonBankCase: true
  })

  const { trackEvent } = useEventTracker()

  const { banks, banksCount } = useBanksRequest()
  const { paymentOptions, paymentOptionsFetching } = usePaymentOptions()

  const isInterac = useMemo(() => paymentMethod === 'INTERAC', [paymentMethod])
  const isBankSelected = useMemo(
    () =>
      !fieldValues.hasBankSelection ||
      (fieldValues.hasBankSelection && fieldValues.getValue('bankChoiceId')) ||
      isInterac,
    [fieldValues, isInterac]
  )

  const fakeLogin = useFakeBankLogin()

  const handleCancelClick = () => {
    if (sessionId) {
      cancelPayment(sessionId)
    }
    void trackEvent(sessionId, 'cancelled_by_payer')
    history.push('/fail')
    return false
  }

  const handleSubmit = () => {
    if (jetonBankSpecialCase && !isJetonWallet(fieldValues.getValue('bankChoiceId'))) {
      fakeLogin.reset()
      setStep(FAKE_BANK_LOGIN_STEP)
      return
    }

    if (paymentMethod && isBankSelected && !checkoutPayFetching) {
      handlePay()
    }
  }

  const getFakeBankIndex = (): number => {
    const index = parseInt(fieldValues.getValue('bankChoiceId'))
    return Number.isFinite(index) ? index - 1 : -1
  }

  const getFakeBankName = (): string => {
    const bankIndex = getFakeBankIndex()
    if (bankIndex >= 0) {
      const banks = FakeJetonbankBanks[countryCode || '']
      return banks?.[bankIndex]?.name ?? ''
    }
    return ''
  }

  const handleFakePay = () => {
    if (fakeLogin.values.areValid()) {
      const bankIndex = getFakeBankIndex()
      if (bankIndex >= 0) {
        const banks = FakeJetonbankBanks[countryCode || '']
        if (banks) {
          void fakeLogin.login(banks[bankIndex]?.url)
        }
      }
    }
  }

  const handleContinueAfterBankSelection = () => {
    if (jetonBankSpecialCase) {
      if (fieldValues.areValid()) {
        setStep(CheckoutFlowStep.CUSTOMER_CONSENT)
      } else {
        scrollToTop()
      }
      return
    }

    if (showFieldsAfterBanks && fieldValues.hasAdditionalFields) {
      setStep(CheckoutFlowStep.ADDITIONAL_FIELDS)
      return
    }

    if (fieldValues.areValid()) {
      handleSubmit()
    } else {
      scrollToTop()
    }
  }

  const handleContinueAfterAdditionalFields = () => {
    if (!showFieldsAfterBanks && fieldValues.hasBankSelection) {
      setStep(CheckoutFlowStep.SELECT_BANK)
    } else {
      handleSubmit()
    }
  }

  const prepareBankOptions = () => {
    const result = []
    result.push({
      value: 'JETON',
      label: tr('METADATA.PAYMENT_OPTIONS.JETON', 'Jeton')
    })

    if (Array.isArray(FakeJetonbankBanks[countryCode])) {
      FakeJetonbankBanks[countryCode].forEach((bank, index) => {
        result.push({
          value: index + 1,
          label: bank.name
        })
      })
    }

    return result
  }

  const handleBankSelected = (code: string) => {
    void trackEvent(sessionId, 'bank_selected', code)
  }

  const prepareBankName = () => {
    const code = fieldValues.getValue('bankChoiceId')
    const selectedBank = banks?.find((bank: SupportedBank) => bank.bankChoiceId === code)
    if (selectedBank) {
      return tr('CHECKOUT.PAYMENT.PAY_BY', 'Pay with {{METHOD}}', {
        METHOD:
          selectedLanguage === 'ko' && countryCode === 'KOR'
            ? selectedBank.name || selectedBank.nameEnglish
            : selectedBank.nameEnglish || selectedBank.name
      })
    }
    return tr('CHECKOUT.PAYMENT.PAY_BY', 'Pay with {{METHOD}}', {
      METHOD: tr(`METADATA.PAYMENT_OPTIONS.${paymentMethod}`, paymentMethod)
    })
  }

  const prepareResponse = useCallback(() => {
    return session?.sessionStatus?.value === 'IN_PROGRESS'
      ? {
          result: parseJson(session?.paymentResult),
          resultType: session?.paymentResultType,
          processingAmount: session?.processingAmount,
          processingCurrency: session?.processingCurrency
        }
      : checkoutPay
  }, [checkoutPay, session])

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }, [])

  const validMethod = useMemo(
    () => isValidString(paymentMethod) && isSingleItemArray(paymentOptions),
    [paymentMethod, paymentOptions]
  )

  const validBankSelected = useMemo(
    () =>
      !fieldValues.hasBankSelection ||
      (fieldValues.hasBankSelection && isValidString(fieldValues.getValue('bankChoiceId'))),
    [fieldValues]
  )

  const validBankSelection = useMemo(
    () =>
      !fieldValues.hasBankSelection ||
      (fieldValues.hasBankSelection && banksCount === 1 && isValidString(fieldValues.getValue('bankChoiceId'))),
    [fieldValues, banksCount]
  )

  const validFields = useMemo(() => {
    if (selectedPaymentOption) {
      const { fields } = selectedPaymentOption
      if (isValidArray(fields)) {
        const hasFieldsToEdit = fields.some((item: CheckoutMethodField) => {
          const { name, prefilledValue, displayIfPrefilled } = item
          return (!prefilledValue || displayIfPrefilled) && name !== 'bankChoiceId'
        })
        return !hasFieldsToEdit
      }
    }
    return true
  }, [selectedPaymentOption])

  const isFirstBankwireInKorea = useMemo(
    () =>
      countryCode === 'KOR' &&
      selectedPaymentOption?.paymentMethod === 'BANKWIRE' &&
      !selectedPaymentOption.tokenizedBankAccount,
    [countryCode, selectedPaymentOption]
  )

  const isSecondBankwireInKorea = useMemo(
    () =>
      countryCode === 'KOR' &&
      selectedPaymentOption?.paymentMethod === 'BANKWIRE' &&
      selectedPaymentOption.tokenizedBankAccount,
    [countryCode, selectedPaymentOption]
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger only when method, bank selection and additional fields are valid
  useEffect(() => {
    if (
      initialized &&
      !triggeredRef.current &&
      !checkoutPayFetching &&
      !jetonBankSpecialCase &&
      validMethod &&
      validBankSelection &&
      validFields
    ) {
      triggeredRef.current = true
      handlePay()
    }
  }, [initialized, validMethod, validBankSelection, validFields])

  // useEffect(() => {
  //   if (isValidArray(paymentOptions) && isValidArray(fields)) {
  //     const data = {}
  //     if (isJetonWallet(formValues.getValue('bankChoiceId'))) {
  //       const jetonFields = paymentOptions.find((option: any) => isJetonWallet(option.paymentMethod))?.fields
  //       internalSetFields(jetonFields)
  //       isValidArray(jetonFields) &&
  //         jetonFields.forEach((item: CheckoutMethodField) => {
  //           const { name, prefilledValue: value, required } = item
  //           data[name] = { value, required, ...prepareValidations(item) }
  //         })
  //       formValues.reset(data)
  //       formValues.setValue('bankChoiceId', 'JETON')
  //     } else {
  //       internalSetFields(selectedPaymentOption?.fields)
  //       const bankChoiceId = formValues.getValue('bankChoiceId')
  //       isValidArray(selectedPaymentOption?.fields) &&
  //         selectedPaymentOption.fields.forEach((item: CheckoutMethodField) => {
  //           if (item.name !== 'bankChoiceId') {
  //             const { name, prefilledValue: value, required } = item
  //             data[name] = { value, required, ...prepareValidations(item) }
  //           }
  //         })
  //       jetonBankSpecialCase && formValues.reset(data)
  //       jetonBankSpecialCase && formValues.setValue('bankChoiceId', bankChoiceId)
  //     }
  //   }
  // }, [formValues.getValue('bankChoiceId'), paymentOptions, fields])

  useEffect(() => {
    if (checkoutPayError) {
      scrollToTop()
      fieldValues.handleErrors(checkoutPayError, {
        'cardDetails.cardNumber': 'cardNumber',
        'cardDetails.expiryMonth': 'expiryMonth',
        'cardDetails.expiryYear': 'expiryYear',
        'cardDetails.cvv': 'cvv'
      })
    }
  }, [checkoutPayError, scrollToTop])

  return (
    <div className='checkout-wrapper'>
      <div className='checkout-container'>
        <CheckoutHeaderBack />

        <CheckoutHeader>
          <div className='left'>
            <span className='amount'>{session?.showAmount || session?.amount}</span>
            <span className='currency'>{session?.showCurrency || session?.currency}</span>
          </div>
          <div className='right'>
            <LanguageMenu />
          </div>
        </CheckoutHeader>

        {sessionFetching || !initialized || (triggeredRef.current && step !== CheckoutFlowStep.DEPOSIT_RESULT) ? (
          <CheckoutBody>
            <CustomPreloader />
          </CheckoutBody>
        ) : (
          <>
            {step === CheckoutFlowStep.SELECT_BANK ||
            step === CheckoutFlowStep.SELECT_METHOD ||
            (step === CheckoutFlowStep.ADDITIONAL_FIELDS && !showFieldsAfterBanks) ? (
              <CheckoutBody>
                <ErrorDisplay error={[sessionError, checkoutPayError]} />

                {!hasOnlyOneCountry ? (
                  <CheckoutInputWrapper className='mt-3'>
                    <Input
                      type='select'
                      name='countryCode'
                      label={tr('CHECKOUT.PAYMENT.COUNTRY_PLACEHOLDER', 'Select country')}
                      options={checkoutCountries?.map((country: CountryChoice) => ({
                        value: country.countryCode,
                        label: country.name
                      }))}
                      value={countryCode}
                      onChange={(_name: string, value: string) => setCountryCode(value)}
                      hideErrorLine
                      data-test='bank-search-input'
                    />
                  </CheckoutInputWrapper>
                ) : (
                  <div className='mb-2' />
                )}

                <SelectMethod
                  method={paymentMethod}
                  setMethod={setPaymentMethod}
                  paymentOptions={paymentOptions}
                  errorVisible={countryCode && !paymentOptionsFetching}
                  jetonBankSpecialCase={jetonBankSpecialCase}
                />

                {isFirstBankwireInKorea ? (
                  <CheckoutInputWrapper>
                    <WarningMessageWrapper>
                      <p>
                        {tr(
                          'CHECKOUT.BANKWIRE.KOREA_WARNING1',
                          'Please note that the first time, the payment process may take a little longer than usual while we verify your bank account. Future transactions will be faster.'
                        )}
                      </p>
                      <p>
                        {tr(
                          'CHECKOUT.BANKWIRE.KOREA_WARNING2',
                          'Please make sure to enter the below bank account information exactly as it appears in your online banking, or your transaction may fail.'
                        )}
                      </p>
                    </WarningMessageWrapper>
                  </CheckoutInputWrapper>
                ) : null}

                {isSecondBankwireInKorea ? (
                  <div className='title'>
                    {tr('CHECKOUT.BANKWIRE.PAY_WITH_LAST_ACCOUNT', 'Pay with {{BANK_NAME}} {{ACCOUNT_NUMBER}}', {
                      BANK_NAME: selectedPaymentOption?.tokenizedBankAccount?.bankName || '',
                      ACCOUNT_NUMBER: selectedPaymentOption?.tokenizedBankAccount?.accountNumber || '****'
                    })}
                  </div>
                ) : null}

                {(!showFieldsAfterBanks && fieldValues.hasAdditionalFields && !isSecondBankwireInKorea) ||
                (isValidArray(fieldValues.fields) && fieldValues.getValue('bankChoiceId') && jetonBankSpecialCase) ? (
                  <AdditionalFieldsStep
                    countryCode={countryCode}
                    paymentMethod={paymentMethod}
                    fieldValues={fieldValues}
                  />
                ) : null}

                {jetonBankSpecialCase && (
                  <div className='pl-4 pr-4'>
                    <Input
                      type='select'
                      label={tr('CHECKOUT.PAYMENT.BANK', 'Bank')}
                      {...fieldValues.registerInput('bankChoiceId', handleBankSelected)}
                      options={prepareBankOptions()}
                      required={true}
                      hideErrorLine
                    />
                  </div>
                )}

                {fieldValues.hasBankSelection && !isSecondBankwireInKorea && !jetonBankSpecialCase ? (
                  <>
                    {!showFieldsAfterBanks && fieldValues.hasAdditionalFields ? (
                      <div className='title'>{tr('CHECKOUT.PAYMENT.BANK', 'Bank')}</div>
                    ) : null}
                    <SelectBankStep
                      sessionId={sessionId}
                      countryCode={countryCode}
                      fieldValues={fieldValues}
                      onSubmit={handleContinueAfterBankSelection}
                    />
                  </>
                ) : null}

                <ButtonsFooter
                  disabled={!paymentMethod || (!validBankSelected && !isSecondBankwireInKorea) || checkoutPayFetching}
                  onSubmit={() => {
                    if (isSecondBankwireInKorea) {
                      handleContinueAfterAdditionalFields()
                    } else {
                      handleContinueAfterBankSelection()
                    }
                  }}
                  onCancel={handleCancelClick}
                  showCancel={session?.showCancelButton}
                />
              </CheckoutBody>
            ) : null}

            {step === CheckoutFlowStep.CUSTOMER_CONSENT && (
              <CheckoutBody>
                <CustomerConsentStep />

                <ButtonsFooter
                  disabled={checkoutPayFetching}
                  onSubmit={handleSubmit}
                  onCancel={handleCancelClick}
                  showCancel={session?.showCancelButton}
                  submitCaption={tr('CHECKOUT.BUTTONS.CONFIRM', 'Confirm')}
                />
              </CheckoutBody>
            )}

            {step === CheckoutFlowStep.ADDITIONAL_FIELDS && showFieldsAfterBanks && !isSecondBankwireInKorea ? (
              <CheckoutBody>
                <span className='back-button' onClick={() => setStep(CheckoutFlowStep.SELECT_BANK)}>
                  <BackIcon />
                </span>

                {paymentMethod ? (
                  <div className='title' data-test={`pay-with-${paymentMethod}-fields`}>
                    {prepareBankName()}
                  </div>
                ) : null}

                <ErrorDisplay error={[sessionError, checkoutPayError]} />

                <AdditionalFieldsStep
                  countryCode={countryCode}
                  paymentMethod={paymentMethod}
                  fieldValues={fieldValues}
                />

                <ButtonsFooter
                  disabled={checkoutPayFetching}
                  onSubmit={handleContinueAfterAdditionalFields}
                  onCancel={handleCancelClick}
                  showCancel={session?.showCancelButton}
                />
              </CheckoutBody>
            ) : null}

            {step === CheckoutFlowStep.INTERNAL_EXECUTION ? <InternalExecutionStep /> : null}

            {step === FAKE_BANK_LOGIN_STEP && (
              <CheckoutBody>
                <div className='pl-4 pr-3 mt-2 mb-2'>
                  <div style={{ fontSize: '1.8rem', fontWeight: '500', marginBottom: '2rem' }}>
                    {tr('CHECKOUT.BANK_LOGIN_REQUEST.TITLE', 'Login to')} {getFakeBankName()}
                  </div>

                  <Input
                    type='text'
                    label={tr('CHECKOUT.BANK_LOGIN_REQUEST.USERNAME', 'Username')}
                    {...fakeLogin.values.registerInput('username')}
                    hideErrorLine
                    autoComplete='off'
                  />

                  <Input
                    type='password'
                    label={tr('CHECKOUT.BANK_LOGIN_REQUEST.PASSWORD', 'Password')}
                    {...fakeLogin.values.registerInput('password')}
                    hideErrorLine
                    autoComplete='off'
                  />

                  <ErrorDisplay error={fakeLogin.error} />

                  <div className='buttons'>
                    <Button
                      type='button'
                      color='primary'
                      text={tr('CHECKOUT.BANK_LOGIN_REQUEST.LOGIN', 'Login')}
                      onClick={handleFakePay}
                      loading={fakeLogin.loading}
                    />
                    <div className='mb-2' />
                    <Button
                      type='button'
                      color='secondary'
                      text={tr('CHECKOUT.BANK_LOGIN_REQUEST.CANCEL', 'Cancel')}
                      onClick={() => setStep(CheckoutFlowStep.SELECT_BANK)}
                      disabled={fakeLogin.loading}
                    />
                  </div>
                </div>
              </CheckoutBody>
            )}

            {step === CheckoutFlowStep.DEPOSIT_RESULT ? (
              <DepositResult response={prepareResponse()} session={session} sessionId={sessionId} />
            ) : null}

            {Boolean(sessionId) && (
              <div className={styles['checkout-footer']}>
                {isValidFunction(modalShow) && (
                  <div
                    className={styles['footer-link']}
                    onClick={() => modalShow(null)}
                    data-test='report-a-problem-button'
                  >
                    {tr('CHECKOUT.PROBLEM.TITLE', 'Report a Problem')}
                  </div>
                )}

                {Boolean(session?.returnUrl) && (
                  <a className={styles['footer-link']} href={session?.returnUrl} target='_top'>
                    &larr; {tr('CHECKOUT.PAYMENT.BACK_TO_MERCHANT', 'Back to merchant')}
                  </a>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
